import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { WithId, IMapPosition, POSITION_TYPE } from 'transfix-maps/types'
import Card from 'transfix-ui/components/Card'
import { TrackingTag } from 'transfix-ui/components/TrackingTag/TrackingTag'
import { Box, Typography } from 'transfix-ui/core'
import { IDriverLocation, INextStop } from 'types/Location'
import { getPath } from 'utils/actions/path'
import { Bullet } from 'components/atoms'
import { PATH_LOCATIONS_LIMIT } from 'constants/locations'
import { IMapEnhancedStopProps } from '../types'
import { pathStateSelector, shipmentStateSelector } from '../utils/selectors/ShipmentMap'
import DetentionReviewMapComponent from './DetentionReviewMapComponent'

export const getPositions = (locations: IDriverLocation[]): WithId<IMapPosition>[] => {
  return locations.map((point, index) => {
    const { latitude, longitude, phoneTimestamp, sourceType, timezone } = point || {}
    const positionType = index === 0 ? POSITION_TYPE.LAST_PING : POSITION_TYPE.PING
    return {
      type: positionType,
      coords: {
        lat: parseFloat(latitude),
        lon: parseFloat(longitude),
      },
      /**
       * Data object needs an `id` prop which is used later to find the right item
       * in the positions array
       * @note I think `point.id` is sometimes undefined, so using index here is better
       */
      data: {
        id: index,
        phoneTimestamp,
        sourceType,
        timezone,
      },
    }
  })
}

export const getStops = (stops: INextStop[]): IMapEnhancedStopProps[] => {
  return stops.map(stop => {
    const { facility, stopSequence, type } = stop
    const { latitude, longitude } = facility

    return {
      type,
      coords: {
        lat: parseFloat(latitude),
        lon: parseFloat(longitude),
      },
      sequence: stopSequence,
      data: { ...stop },
    }
  })
}

export const DetentionReviewMap: FunctionComponent = () => {
  const [showAllPings, setShowAllPings] = useState<boolean>(false)

  const shipmentData = useSelector(shipmentStateSelector)
  const pathData = useSelector(pathStateSelector)
  const { shipment } = shipmentData || {}
  const { path } = pathData || {}
  const { stops = [], trackingStatus, id: shipmentId, uuid: shipmentUuid, status } = shipment || {}

  const dispatch = useDispatch()

  const dispatchGetPath = (shipmentUuid: string) => dispatch(getPath(shipmentUuid))

  // This component's (old) parent and siblings are using redux, so the pattern is continued here
  useEffect(() => {
    dispatchGetPath(shipmentUuid)
  }, [shipmentUuid])

  const viewChangeHandler = (view: boolean) => setShowAllPings(view)

  const locations = showAllPings ? path : path.slice(0, PATH_LOCATIONS_LIMIT)
  const positions = useMemo(() => getPositions(locations), [locations])
  const geoStops = useMemo(() => getStops(stops), [stops])

  return (
    <Card>
      <Box display='flex' alignItems='center'>
        <a
          href={`${__ROOT__}/shipment-details/${shipmentId}?tab=scheduling`}
          target='_blank'
          rel='noopener noreferrer'>
          Shipment #{shipmentId}
        </a>
        <Bullet />
        <Typography variant='subtitle2' color='textSecondary'>
          {status.name}
        </Typography>
      </Box>
      <Box display='flex' mt={0.5} alignItems='center'>
        <Typography variant='body1'>Shipment Tracking:</Typography>
        <Box display='flex' ml={0.5} alignItems='center'>
          <TrackingTag trackingStatus={trackingStatus} tooltipPosition='bottom' />
        </Box>
      </Box>
      <Box height='600px'>
        <DetentionReviewMapComponent
          stops={geoStops}
          positions={positions}
          onViewChange={viewChangeHandler}
        />
      </Box>
    </Card>
  )
}
